import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonInputPasswordToggle,
  IonPage,
  IonRouterLink,
  IonSpinner,
  IonTitle,
  IonToolbar
} from '@ionic/react';
import { motion } from 'framer-motion';
import { arrowBack, logoApple } from 'ionicons/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
const CustomSignIn: React.FC = () => {

  const { t } = useTranslation(['Auth', 'MGP']);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const history = useHistory();
  const handleSubmit = async () => {
    setError('');
    setIsLoading(true);

    try {
      const result = await FirebaseAuthentication.signInWithEmailAndPassword({ email: identifier, password: password });
      if (result && result.user) {
        // Successful sign-in, redirect to dashboard
        // The App.tsx routing will handle checking if profile exists
        // and redirect appropriately
        history.push('/dashboard');
      }
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (err: any) {
      console.error('Error:', err);
      const errorCode = (err.code as string).replace('auth/', '');
      const errorMessage = t(`Auth:${errorCode}`) as string;
      setError(errorMessage || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar color="light" className="md:hidden px-2 bg-card">
          <IonButtons slot="start">
            <IonButton color="medium" routerLink='/mobile'>
              <IonIcon icon={arrowBack} className="w-5 h-5" />
            </IonButton>
          </IonButtons>
          <IonTitle>{t('Auth:sign_in')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color="light" scrollY={false}>
        <div className="flex flex-col p-3 justify-center items-center h-full md:bg-[url('/assets/images/bg/bg3.webp')] bg-cover bg-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md space-y-6 md:bg-card md:p-8 rounded-xl md:mt-[-64px]"
          >
            <div className="space-y-4">
              <h2 className="text-2xl md:block hidden font-semibold text-foreground">{t('Auth:sign_in')}</h2>
              <div className="space-y-4">
                <IonInput
                  type="email"
                  value={identifier}
                  shape='round'
                  onIonChange={(e) => setIdentifier(e.detail.value!)}
                  placeholder="E-mail ou nom d'utilisateur"
                  className="h-12 rounded-2xl border border-muted-foreground/20 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary overflow-hidden"
                  style={{
                    '--padding-start': '1rem',
                    '--padding-end': '1rem',
                  }}
                  autocomplete="email"
                  enterkeyhint="next"
                />
                <IonInput
                  type="password"
                  value={password}
                  onIonChange={(e) => setPassword(e.detail.value!)}
                  placeholder={t('Auth:password')}
                  className="h-12 rounded-2xl border border-muted-foreground/20 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary overflow-hidden"
                  style={{
                    '--padding-start': '1rem',
                    '--padding-end': '1rem',
                  }}
                  autocomplete="current-password"
                  enterkeyhint="done"
                >
                  <IonInputPasswordToggle color="medium" slot="end"></IonInputPasswordToggle>
                </IonInput>
              </div>


              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              <div className="flex justify-end mb-2">
                <IonRouterLink routerLink="/forgot-password" className="text-sm text-muted-foreground hover:underline">
                  {t('Auth:forgot_password') || 'Forgot Password?'}
                </IonRouterLink>
              </div>

              <IonButton
                expand="block"
                shape="round"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <IonSpinner name="dots" />
                ) : (
                  t('Auth:continue')
                )}
              </IonButton>

              <div className="flex items-center gap-4 py-2">
                <div className="flex-1 h-px bg-muted" />
                <span className="text-muted-foreground">{t('MGP:or')}</span>
                <div className="flex-1 h-px bg-muted" />
              </div>

              <IonButton
                color="transparent"
                fill='clear'
                className="w-full bg-black rounded-full"
                shape="round"
                disabled={isAppleLoading}
                onClick={async () => {
                  try {
                    setIsAppleLoading(true);
                    setError('');
                    const user = await FirebaseAuthentication.signInWithApple();
                    if (user) {
                      history.push('/dashboard')
                    }
                  } catch (err: any) {
                    console.error('Error:', err);
                    setError(err.message || t('sign_in_error'));
                  } finally {
                    setIsAppleLoading(false);
                  }
                }}
              >
                {isAppleLoading ? (
                  <IonSpinner color='light' name="dots" className="text-white" />
                ) : (
                  <div className="flex items-center justify-center text-white gap-2 w-full">
                    <IonIcon
                      icon={logoApple}
                      className="size-6 relative -top-[1px]"
                      style={{ margin: 0 }}
                    />
                    <span>Apple</span>
                  </div>
                )}
              </IonButton>

              <IonButton
                color="tertiary"
                fill='solid'
                className="w-full border rounded-full text-black"
                shape="round"
                disabled={isGoogleLoading}
                onClick={async () => {
                  try {
                    setIsGoogleLoading(true);
                    setError('');
                    const user = await FirebaseAuthentication.signInWithGoogle();
                    if (user) {
                      history.push('/dashboard')
                    }
                  } catch (err: any) {
                    console.error('Error:', err);
                    setError(err.message || t('sign_in_error'));
                  } finally {
                    setIsGoogleLoading(false);
                  }
                }}
              >
                {isGoogleLoading ? (
                  <IonSpinner color='dark' name="dots" />
                ) : (
                  <div className="flex items-center justify-center gap-2 w-full">
                    <img src="/assets/images/social/google.svg" className="w-5 h-5" />
                    <span>Google</span>
                  </div>)}
              </IonButton>

              <div className="text-center text-sm">
                <span className="text-muted-foreground">Vous n'avez pas encore de compte ? </span>
                <IonRouterLink routerLink="/sign-up" className="text-foreground font-medium">
                  {t('Auth:sign_up')}
                </IonRouterLink>
              </div>
            </div>
          </motion.div>
        </div>
      </IonContent >
    </IonPage >
  );
};

export default CustomSignIn;
